import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/app/Navbar";
import ListingCard from "@/components/app/ListingCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, SlidersHorizontal, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import LocationOnboardingModal from "@/components/app/LocationOnboardingModal";
import ListingsMap from "@/components/app/ListingsMap";

const LOCALITIES = ["Any", "Vijay Nagar", "Palasia", "Bhawarkuan", "AB Road", "Rau", "Sudama Nagar",
                    "Old Palasia", "Nipania", "MR-10", "Bengali Square", "Scheme 78", "Geeta Bhawan"];
const BHKS = ["Any", "Studio", "1BHK", "2BHK", "3BHK"];
const FURN = ["Any", "unfurnished", "semi", "full"];
const GENDER = ["any", "male", "female"];
const ROOM = ["Any", "private", "shared", "entire"];
const TYPE = ["Any", "Entire Property", "Private Room", "Shared Room", "PG", "Hostel", "Flatmate Needed"];

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const { user, refresh } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [locality, setLocality] = useState(params.get("locality") || "Any");
  const [bhk, setBhk] = useState("Any");
  const [furnishing, setFurnishing] = useState("Any");
  const [genderPref, setGenderPref] = useState("any");
  const [roomType, setRoomType] = useState("Any");
  const [listingType, setListingType] = useState("Any");
  const [vegOnly, setVegOnly] = useState(false);
  const [petsOk, setPetsOk] = useState(false);
  const [rentRange, setRentRange] = useState([5000, 40000]);
  const [sort, setSort] = useState("recent");
  const [showMap, setShowMap] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  // Try to match a Nominatim locality string to one of the known LOCALITIES
  const matchLocality = (nominatimLocality) => {
    if (!nominatimLocality) return null;
    const lower = nominatimLocality.toLowerCase();
    return LOCALITIES.find(
      (l) => l !== "Any" && lower.includes(l.toLowerCase())
    ) || null;
  };

  // Show onboarding for new users; for returning users pre-filter by their saved locality
  useEffect(() => {
    if (params.get("onboarding") === "1" || (user && !user.latitude && !user.longitude)) {
      setOnboardingOpen(true);
    }
  }, [params, user]);

  const handleOnboardingClose = () => {
    setOnboardingOpen(false);
    if (params.get("onboarding")) {
      const nextParams = new URLSearchParams(params);
      nextParams.delete("onboarding");
      setParams(nextParams);
    }
  };

  // Called when the onboarding modal saves — apply locality filter immediately
  const handleOnboardingSaved = ({ city, locality } = {}) => {
    refresh();
    const matched = matchLocality(locality);
    if (matched) {
      setLocality(matched);
    }
  };

  const query = useMemo(() => {
    const p = { sort, min_rent: rentRange[0], max_rent: rentRange[1] };
    if (q) p.q = q;
    if (locality !== "Any") p.locality = locality;
    if (bhk !== "Any") p.bhk = bhk;
    if (furnishing !== "Any") p.furnishing = furnishing;
    if (genderPref !== "any") p.gender_pref = genderPref;
    if (roomType !== "Any") p.room_type = roomType;
    if (listingType !== "Any") p.listing_category = listingType;
    if (vegOnly) p.food_pref = "veg";
    if (petsOk) p.pets_ok = true;
    return p;
  }, [q, locality, bhk, furnishing, genderPref, roomType, listingType, vegOnly, petsOk, rentRange, sort]);

  useEffect(() => {
    setLoading(true);
    api.get("/listings", { params: query }).then((r) => {
      setListings(r.data.listings);
    }).finally(() => setLoading(false));
  }, [query]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-2">Discover</div>
            <h1 className="font-display text-3xl md:text-4xl font-medium text-stone-900">
              {params.get("city") ? `Rooms & flats in ${params.get("city")}` : "Rooms & flats"}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="rounded-full gap-2 border-stone-300 h-10 px-4 text-xs font-semibold uppercase tracking-wider"
              onClick={() => setShowMap(!showMap)}
            >
              <MapPin className="w-4 h-4 text-brand" />
              {showMap ? "Show list" : "Show map"}
            </Button>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-44 rounded-full" data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Most recent</SelectItem>
                <SelectItem value="price_asc">Price: low to high</SelectItem>
                <SelectItem value="price_desc">Price: high to low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Filters */}
          <aside className="lg:col-span-3 space-y-5">
            <div className="p-5 bg-white border border-stone-200 rounded-2xl">
              <div className="flex items-center gap-2 mb-4">
                <SlidersHorizontal className="w-4 h-4 text-brand" />
                <div className="font-display font-medium">Filters</div>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs">Search</Label>
                  <div className="relative mt-1">
                    <Search className="w-4 h-4 absolute left-3 top-3 text-stone-400" />
                    <Input data-testid="filter-search" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Title, locality…" />
                  </div>
                </div>
                <FilterSelect label="Category" value={listingType} onChange={setListingType} options={TYPE} testid="filter-type" />
                <FilterSelect label="Locality" value={locality} onChange={setLocality} options={LOCALITIES} testid="filter-locality" />
                <FilterSelect label="BHK" value={bhk} onChange={setBhk} options={BHKS} testid="filter-bhk" />
                <FilterSelect label="Furnishing" value={furnishing} onChange={setFurnishing} options={FURN} testid="filter-furnishing" formatter={(v) => v.charAt(0).toUpperCase() + v.slice(1)} />
                <FilterSelect label="Room type" value={roomType} onChange={setRoomType} options={ROOM} testid="filter-room" />
                <FilterSelect label="Gender pref" value={genderPref} onChange={setGenderPref} options={GENDER} testid="filter-gender" formatter={(v) => v.charAt(0).toUpperCase() + v.slice(1)} />

                <div>
                  <Label className="text-xs">Rent range (₹)</Label>
                  <div className="pt-3 px-1">
                    <Slider
                      data-testid="filter-rent"
                      value={rentRange} onValueChange={setRentRange}
                      min={3000} max={60000} step={1000}
                    />
                    <div className="flex justify-between text-xs text-stone-500 mt-2">
                      <span>₹{rentRange[0].toLocaleString()}</span>
                      <span>₹{rentRange[1].toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="pt-2 space-y-2">
                  <label className="flex items-center gap-2 text-sm text-stone-700">
                    <Checkbox data-testid="filter-veg" checked={vegOnly} onCheckedChange={setVegOnly} /> Vegetarian only
                  </label>
                  <label className="flex items-center gap-2 text-sm text-stone-700">
                    <Checkbox data-testid="filter-pets" checked={petsOk} onCheckedChange={setPetsOk} /> Pets allowed
                  </label>
                </div>

                <Button variant="ghost" className="w-full text-stone-600" data-testid="filter-reset" onClick={() => {
                  setQ(""); setLocality("Any"); setBhk("Any"); setFurnishing("Any");
                  setGenderPref("any"); setRoomType("Any"); setListingType("Any");
                  setVegOnly(false); setPetsOk(false); setRentRange([5000, 40000]);
                }}>Reset filters</Button>
              </div>
            </div>
          </aside>

          {/* Results */}
          <main className="lg:col-span-9">
            {showMap ? (
              <ListingsMap listings={listings} />
            ) : loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={`skeleton-${i}`} className="rounded-2xl border border-stone-200 bg-white h-72 animate-pulse" />
                ))}
              </div>
            ) : listings.length === 0 ? (
              <div className="text-center py-24 text-stone-500 border border-dashed border-stone-300 rounded-2xl">
                No listings match your filters. Try loosening them.
              </div>
            ) : (
              <>
                <div className="text-sm text-stone-500 mb-4">{listings.length} listings</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {listings.map((l) => <ListingCard key={l.id} listing={l} />)}
                </div>
              </>
            )}
          </main>
        </div>
      </div>
      <LocationOnboardingModal
        isOpen={onboardingOpen}
        onClose={handleOnboardingClose}
        onSaved={handleOnboardingSaved}
      />
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid, formatter }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="mt-1"><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{formatter ? formatter(o) : o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
